import { useCallback, useEffect, useMemo } from 'react';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import useUpdateAutomationVersion from '@/hooks/api/automationVersions/useUpdateAutomationVersion.ts';
import useRetrieveRunAutomationVersion from '@/hooks/api/runs/useRetrieveRunAutomationVersion.ts';
import { AutomationStep } from '@/types/automationSteps.ts';
import { isOutputPinned, togglePinnedOutput } from '@/utils/pinnedOutputs.ts';

export function usePinnedOutput(
  step: AutomationStep | undefined,
  outputId?: string | null,
  runId?: string,
) {
  const { data: latestVersion } = useRetrieveLatestAutomationVersion();
  const { mutate: updatePinnedOutputs, isSuccess } = useUpdateAutomationVersion();

  const { data: runAutomationVersion, refetch } = useRetrieveRunAutomationVersion(runId);

  useEffect(() => {
    if (isSuccess && runId) refetch();
  }, [isSuccess, runId, refetch]);

  const version = runAutomationVersion ?? latestVersion;

  const pinned = useMemo(
    () => isOutputPinned(version, step?.step_id, outputId),
    [outputId, step?.step_id, version],
  );

  const toggle = useCallback(() => {
    if (!version || !step) return;
    updatePinnedOutputs({
      versionId: version.id,
      pinned_outputs: togglePinnedOutput(version, step.step_id, outputId),
    });
  }, [outputId, step, updatePinnedOutputs, version]);

  return { isPinnedOutput: pinned, togglePinnedOutput: toggle };
}
